import { ISourceDataStore } from '@stores/sourceDataStore';
import { IMessageStore } from '@stores/messages';
import React, { FunctionComponent, useCallback, useEffect, useState } from 'react';
import _, { findIndex } from 'lodash';
import cloudSourceSettings from '@components/sources/source/cloudSource/cloudSourcesConfig';
import { inject, observer } from 'mobx-react';
import { IWorkspace } from '@stores/workspace';
import { isValidationLoadingType } from '@components/sources/source/addSource';
import Modal from '@ui-library/modal/custom';
import SourceFactory from '@components/sources/sourceFactory';
import { FixMe } from '@utils/types';
import classes from './credential-popup.module.scss';

interface ICredentialsState {
  accountId?: string;
  accountInfo?: FixMe;
  updated?: boolean;
  submit?: boolean;
}

interface OwnProps {
  onCancel: () => void;
  sourceDataStore?: ISourceDataStore;
  messagesStore?: IMessageStore;
  workspaceStore?: IWorkspace;
}

type Props = OwnProps;

const CredentialPopup: FunctionComponent<Props> = (props: Props) => {
  const { sourceDataStore: source, workspaceStore, messagesStore, onCancel } = props;
  const [credentials, setCredentials] = useState<ICredentialsState>({});
  const [isValidationLoading, setisValidationLoading] = useState<isValidationLoadingType>(
    isValidationLoadingType.hide,
  );
  const sourceInstance = SourceFactory(source?.sourceDef.category);
  const isValidationDisable = !(
    sourceInstance.enableCredentialValidations() &&
    (source?.config.isCredentialsValidationSupported ||
      (source?.destinations?.length
        ? source?.config?.syncBehaviour?.mode
        : _.intersection(
            workspaceStore?.whActionsSyncBehaviours,
            source?.sourceDef.options?.syncBehaviours,
          )?.length))
  );
  const [isNextEnabled, setNextEnabled] = useState(false);

  const isNewAccountName = async (account_name: string) => {
    const { accounts } = await source!.getCredentialInfo();
    const accountIndex = findIndex(
      accounts,
      (account: FixMe) =>
        account.options?.rudder_account_name &&
        account.options?.rudder_account_name === account_name,
    );
    return accountIndex === -1;
  };

  const isOauthSource = (name: string) =>
    cloudSourceSettings[name as keyof typeof cloudSourceSettings]?.auth?.type === 'OAuth';

  const handleChange = useCallback(
    async (accountId: string, accountInfo: FixMe, isValid?: boolean, submit?: boolean) => {
      if (
        isValidationLoading === isValidationLoadingType.passed ||
        isValidationLoading === isValidationLoadingType.failed ||
        isValidationLoading === isValidationLoadingType.notStarted
      ) {
        checkValid(
          isValidationLoadingType.notStarted,
          Boolean(credentials.accountId && !accountId),
        );
        /* when the selected accountId changes, make the validation
      run again when pressed on NEXT. */
      } else {
        setNextEnabled(!!isValid); // setting the save button based on isvalid.
      }
      setCredentials({ accountId, accountInfo, updated: true, submit });
    },
    [setCredentials, source?.sourceDef, isValidationLoading, setNextEnabled],
  );

  const checkAccountId = useCallback(async () => {
    const { accountId, accountInfo } = credentials;
    if (!accountId && accountInfo) {
      return sourceInstance.validateAccount({
        source,
        isNewAccountName,
        isOauthSource,
        accountInfo,
        setCredentials,
        showErrorMessage: messagesStore?.showErrorMessage,
        accountId,
        valid: isNextEnabled,
      });
    }
    return accountId;
  }, [credentials, isNextEnabled]);

  const handleSave = useCallback(async () => {
    if (
      isValidationLoading !== isValidationLoadingType.passed &&
      isValidationLoading !== isValidationLoadingType.disable &&
      !isValidationDisable
    ) {
      checkValid(isValidationLoadingType.loading);
      return;
    }
    if (!isNextEnabled || !credentials?.updated) {
      return;
    }
    const accountId = await checkAccountId();
    if (!accountId) {
      return;
    }
    await source!.updateConfig({ accountId });
    props.onCancel();
  }, [credentials, isValidationLoading, checkAccountId, source?.updateConfig, isNextEnabled]);

  useEffect(() => {
    if (credentials?.submit) {
      handleSave();
    }
  }, [credentials?.submit, handleSave]);

  const checkValid = (result?: isValidationLoadingType, isAccountDeleted?: boolean) => {
    if (isValidationDisable) {
      setisValidationLoading(isValidationLoadingType.hide);
      return;
    }
    setisValidationLoading(result || isValidationLoadingType.hide);
    setNextEnabled(
      (result === isValidationLoadingType.passed ||
        result === isValidationLoadingType.notStarted ||
        result === isValidationLoadingType.failed) &&
        !isAccountDeleted,
    );
  };

  const authComp = (
    <div className={classes.auth_container}>
      <div className={classes.auth_container__wrapper}>
        {sourceInstance.getAuthComponent({
          checkValid,
          source,
          credentials,
          isValidationLoading,
          isValidationDisable,
          handleChange,
        })}
      </div>
    </div>
  );

  return (
    <Modal
      modalClass={classes.edit_credentials_popup}
      size="xl"
      title="Edit Credentials"
      onClose={onCancel}
      content={authComp}
      saveLabel="Save"
      onSave={handleSave}
      isSaveDisabled={!isNextEnabled}
    />
  );
};

export default inject(
  'sourceDataStore',
  'messagesStore',
  'workspaceStore',
)(observer(CredentialPopup));
